import { TreeList } from "@/regira_modules/treelist";
import { sum, distinct } from "@/regira_modules/utilities/array-utility";
import { getData, isRequired, getRequiredProductNodes, isChoiceProductRoot, isChoiceProduct, isHeaderLine } from "@/infrastructure/data-functions";
import {
  newOrder,
  ensureLatestOrderVersion,
  ensureLegacyOrderVersion,
  getOrderedOffspring,
  hasOrderedOffspring,
  isOrdered,
  ensureRequiredQuantity,
  validateOrderlines,
  save,
  calcTotal,
} from "@/infrastructure/order-functions";
import router from "@/routing";

const getInitialOrderlines = (productTree, webshop) => {
  const requiredProductNodes = getRequiredProductNodes(productTree);
  return requiredProductNodes
    .filter((rpn) => !isChoiceProductRoot(rpn))
    .map((rpn) => ({
      productId: rpn.value.id,
      quantity: rpn.value.requiredQuantity || 1,
      _isRequired: isRequired(rpn.value, webshop),
      _requiredQuantity: rpn.value.requiredQuantity,
    }));
};

export default {
  namespaced: true,
  state: () => ({
    order: newOrder(),
  }),
  getters: {
    klas: (state, getters, rootState, rootGetters) => rootGetters["data/findKlas"](state.order.klasId),
    isNewCustomer: (state) => state.order.isNewCustomer,
    orderlines: (state) => state.order.orderlines,
    contact: (state) => ({ parent: state.order.parent, student: state.order.student, comments: state.order.comments }),
    hasDiscount: (state, getters, rootState) => rootState.webshop.discount > 0,
    hasOrderedOffspring(state, getters) {
      return (productNode) => hasOrderedOffspring(productNode, getters.orderlines);
    },
    isOrdered(state, getters) {
      return (productNode, checkOffspring = true) => isOrdered(productNode, getters.orderlines, checkOffspring);
    },
    selectedIds(state, getters) {
      return (productNode) =>
        getters.orderlines
          .filter((ol) =>
            isChoiceProductRoot(productNode) ? productNode.getOffspring().some((o) => o.value.id === ol.productId) : productNode.value.id === ol.productId
          )
          .map((ol) => ol.productId);
    },
    orderedQuantity(state, getters) {
      return (productNode) => {
        const orderlines = isChoiceProductRoot(productNode)
          ? getters.orderlines.filter((ol) => productNode.getOffspring().some((offspring) => offspring.value.id === ol.productId))
          : getters.orderlines.filter((ol) => ol.productId === productNode.value.id);
        return sum(orderlines, (ol) => ol.quantity);
      };
    },
    // validation
    hasValidKlas(state, getters, rootState, rootGetters) {
      return rootGetters["data/findKlas"](state.order.klasId) != null;
    },
    hasValidOrderlines(state, getters, rootState) {
      return (
        rootState.webshop.ignoreMandatoryProducts ||
        rootState.webshop.hasPreviousPaidOrder ||
        validateOrderlines(getters.orderlines, rootState.data.productTree)
      );
    },
    hasValidContact(state) {
      return state.order.parent.email && state.order.student.email;
    },
    isValidOrder(state, getters) {
      return getters.hasValidKlas && getters.hasValidOrderlines;
    },
    // payment
    hasPaymentStatus(state) {
      return !!state.order.paymentStatus;
    },
    isPaid(state, getters, rootState) {
      // an order is paid when
      // - it has a valid payment
      // - payments are skipped
      // - the total cost (with discount) is zero or below
      return (
        state.order.paymentStatus === "paid" || ((rootState.webshop.skipPayment || getters.totalCostWithDiscount <= 0) && state.order.paymentStatus == "na")
      );
    },
    isClosed(state, getters) {
      return getters.isPaid;
    },
    totalCost: (state, getters, rootState) => {
      return calcTotal(getters.orderlines, rootState.data.productTree);
    },
    totalCostWithDiscount: (state, getters, rootState) => {
      return Math.max(getters.totalCost - (rootState.webshop.discount || 0), 0);
    },
    // email confirmation
    emailConfirmationSuccess: (state) => state.order.mailStatus === "success",
    emailConfirmationFailed: (state) => state.order.mailStatus === "failed",
  },
  mutations: {
    setOrder: (state, payload) => (state.order = payload),
    setKlasId: (state, klasId) => (state.order = { ...state.order, klasId }),
    setOrderlines: (state, payload) => (state.order = { ...state.order, orderlines: payload }),
    setContact: (state, { parent, student, comments }) => (state.order = { ...state.order, parent, student, comments }),
    setPaymentStatus: (state, status) => (state.order = { ...state.order, paymentStatus: status }),
  },
  actions: {
    async submitKlas({ commit, state, rootState }, payload) {
      const { klasId, isNewCustomer } = payload;
      if (!klasId) {
        return;
      }

      const data = await getData(rootState.appId, { klasId });
      commit("data/setKlassen", data.classes, { root: true });

      // reset ordered products?
      if (state.order.klasId !== klasId || state.order.isNewCustomer != isNewCustomer) {
        state.order.isNewCustomer = isNewCustomer;

        commit("setKlasId", klasId);
        commit("data/setCategories", { items: data.categories, isNewCustomer }, { root: true });
        commit("data/setProductTree", { items: data.products, isNewCustomer }, { root: true });

        // initial orderlines: required products (ignore when hasPreviousPaidOrder)
        const initialOrderlines = getInitialOrderlines(
          !rootState.webshop.hasPreviousPaidOrder ? rootState.data.productTree : new TreeList(),
          rootState.webshop
        );

        commit("setOrderlines", initialOrderlines);
      }
    },
    async submitContact({ commit }, contact) {
      commit("setContact", contact);
    },
    setOrder({ commit, rootState, rootGetters }, payload) {
      const compatibleOrder = ensureLatestOrderVersion({ ...payload });
      const order = {
        ...newOrder(),
        ...compatibleOrder,
        orderlines: (compatibleOrder.orderlines || [])
          .map((line) => {
            // make sure _isRequired is known
            if (!("_isRequired" in line)) {
              const product = rootGetters["data/findProduct"](line.productId);
              if (product == null) {
                // product can be null when it's an emptyProduct and the id in the initialOrder (querystring) doesn't match anymore
                console.warn(`could not find product ${line.productId}`);
                return null;
              }
              line._isRequired = product != null && isRequired(product, rootState.webshop);
            }
            return line;
          })
          .filter((ol) => ol != null),
      };

      //console.debug("setOrder", { webshop: rootState.webshop, order: { ...payload } });
      // initial orderlines: required products (ignore when order is paid or hasPreviousPaidOrder)
      const initialOrderlines = getInitialOrderlines(
        !(order.paymentStatus === "paid" || rootState.webshop.hasPreviousPaidOrder) ? rootState.data.productTree : [],
        rootState.webshop
      );
      initialOrderlines.forEach((line) => {
        if (!order.orderlines.some((ol) => ol.productId === line.productId)) {
          // only add item when required or when the initial order is created
          if (order.id == null || line._isRequired) {
            order.orderlines.push(line);
          }
        }
      });

      commit("setOrder", order);
    },
    removeProduct({ dispatch }, productNode) {
      return dispatch("removeProducts", [productNode.value.id])[0];
    },
    removeProducts({ commit, state }, productIds) {
      const newOrderlines = state.order.orderlines.filter((ol) => ol._isRequired || !productIds.some((pId) => ol.productId === pId));

      commit("setOrderlines", newOrderlines);

      return newOrderlines;
    },
    addProduct({ dispatch }, { productNode, quantity }) {
      return dispatch("addProducts", [{ productNode, quantity }])[0];
    },
    async addProducts({ commit, dispatch, state, rootState }, payload) {
      const newOrderlines = [...state.order.orderlines];
      const choiceProductSiblingsToRemove = distinct(
        payload
          .filter((ol) => isChoiceProduct(ol.productNode))
          .flatMap((ol) =>
            ol.productNode.parent
              .getOffspring()
              //.filter((sibling) => sibling.value.id !== ol.productNode.value.id)
              .filter((sibling) => state.order.orderlines.some((ol) => ol.productId === sibling.value.id))
          )
      );
      const productIdsToRemove = payload
        .filter((ol) => ol.quantity <= 0)
        .map((ol) => ol.productNode)
        .concat(choiceProductSiblingsToRemove)
        .map((pn) => pn.value.id);

      for (const ol of payload.filter((e) => e.quantity > 0)) {
        let { productNode, quantity } = ol;

        if (isChoiceProductRoot(productNode)) {
          productNode = getOrderedOffspring(productNode, newOrderlines)[0];
        }

        if (productNode != null && !isHeaderLine(productNode)) {
          const product = productNode.value;
          const index = newOrderlines.findIndex((ol) => ol.productId === product.id);
          const orderline =
            index === -1
              ? {
                  productId: product.id,
                  _isRequired: isRequired(product, rootState.webshop),
                  _requiredQuantity: product.requiredQuantity,
                }
              : newOrderlines[index];

          orderline.quantity = ensureRequiredQuantity(quantity, productNode);

          if (index === -1) {
            newOrderlines.push(orderline);
          } else {
            newOrderlines[index] = {
              ...orderline,
            };
          }
        }
      }

      if (productIdsToRemove.length) {
        await dispatch("removeProducts", productIdsToRemove);
      }

      commit("setOrderlines", newOrderlines);

      return newOrderlines;
    },
    async save({ dispatch, state, rootState, rootGetters }) {
      const appId = rootState.appId;
      const orderToSave = ensureLegacyOrderVersion(state.order, rootGetters["data/products"]);
      const redirectUrl = router.resolve({ name: "bevestiging", query: { appId, orderId: orderToSave.id || "{orderId}" } }).fullPath;
      const { saved, checkoutUrl } = await save(orderToSave, appId, redirectUrl);
      await dispatch("setOrder", saved);

      router.replace({ ...router.currentRoute.value, query: { appId, orderId: saved.id } });

      if (saved.id && checkoutUrl) {
        window.open(checkoutUrl, "_self");
      } else {
        throw Error("Cannot redirect: Missing orderId or checkoutUrl");
      }
      return saved;
    },
  },
};
